import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { faSquareBluesky, faXTwitter, faWhatsapp, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { faShareAlt } from '@fortawesome/free-solid-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons'; // Import Envelope icon

import { ENVIRONMENT } from '@environment';
import { Movie } from '@usheru-hff/shared/domain';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

interface SocialLink {
  iconProp: any;
  platform: string;
  to: string;
}

/**
 * Component that shows links to share a movie in different social platforms.
 *
 * //TODO barbeitoalex propostal - refractor name to something like "movie-social-links".
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - update translate text with an observable to the current language
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-share',
  templateUrl: './share.component.html',
  styleUrls: ['./share.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShareComponent implements OnInit {
  private readonly env = inject(ENVIRONMENT);

  /**
   * @docsNotRequired
   */
  @Input()
  movie?: Movie;

  @Input()
  compacted: boolean;

  @Input()
  shareText = 'public.commons.share';

  /**
   * Content of the shared message.
   */
  @Input()
  message: string;

  /**
   * Subject when shared with mail.
   */
  @Input()
  mailSubject: string;

  /**
   * Subject when shared with mail.
   */
  @Input()
  campaign: string;

  /**
   * @docsNotRequired
   */
  @Input()
  trackingCategory: string;

  /**
   * @deprecard Don't found any use
   */
  @Input()
  trackingExtraProps: any;

  @Output() complete = new EventEmitter<void>();

  /**
   * @docsNotRequired
   *
   * //TODO barbeitoalex propostal - remove the "to". Don't have any use.
   */
  socialLinks: SocialLink[] = [
    {
      iconProp: faXTwitter,
      platform: 'twitter',
      to: '/'
    },
    {
      iconProp: faFacebook,
      platform: 'facebook',
      to: '/'
    },
    {
      iconProp: faWhatsapp,
      platform: 'whatsapp',
      to: '/'
    },
    {
      iconProp: faEnvelope,
      platform: 'mail',
      to: '/'
    }
  ];

  shareIcon = faShareAlt;

  constructor(
    private translations: TranslationsService,
    private trackingService: TrackingService
  ) {
    if (this.env?.showBlueSky) {
      this.socialLinks.push({
        iconProp: faSquareBluesky,
        platform: 'bluesky',
        to: '/'
      });
    }
  }

  ngOnInit(): void {
    // DEFAULT PARAMS
    if (this.movie) {
      if (!this.trackingCategory) this.trackingCategory = 'MovieDetail';
      if (!this.message)
        this.message = this.translations.translate('movie.tellYourFriends').replace('_movie_', this.movie?.title || '');
      if (!this.mailSubject)
        this.mailSubject = this.translations.translate(this.movie.trTitle).replace('_movie_', this.movie?.title || '');
      if (!this.campaign) this.campaign = `Sharing-%movieUrl%`.replace('%movieUrl%', this.movie?.friendlyUrl || '');
    }
  }

  /**
   * @docsNotRequired
   * @param sl
   * @returns
   */
  onShareLink(sl: SocialLink) {
    let message = this.message;
    let subject = this.mailSubject;
    let campaign = this.campaign;
    const url = encodeURIComponent(
      `${window.location.href}?utm_source=${sl.platform}&utm_medium=UserShared&utm_campaign=${campaign}`
    );
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    message = encodeURI(message);
    subject = encodeURI(subject);
    campaign = encodeURI(campaign);

    switch (sl.platform) {
      case 'facebook':
        return `https://facebook.com/sharer/sharer.php?u=${url}`;
      case 'twitter':
        return `https://twitter.com/intent/tweet?text=${message}&url=${url}`;
      case 'whatsapp':
        if (isMobile) {
          return `whatsapp://send?text=${message}%0A${url}`;
        } else {
          return `https://web.whatsapp.com/send?text=${message}%0A${url}`;
        }
      case 'mail':
        return `mailto:?subject=${subject}&body=${message}%0A${url}`;
      default:
        return '';
    }
  }
  /**
   * @docsNotRequired
   * @param sl
   */
  onShareLinkClicked(sl: SocialLink) {
    const extraProps = {};

    if (this.movie) {
      extraProps['idMovie'] = this.movie.id;
      extraProps['movie'] = this.movie.title;
      extraProps['imdb'] = this.movie?.imdb;
    }

    this.trackingService.trackEvent({
      action: 'ShareSocial',
      properties: Object.assign(
        {
          category: this.trackingCategory,
          label: this.onShareLink(sl),
          socialPlatform: sl.platform
        },
        extraProps
      )
    });

    this.complete.emit();
  }
}
